import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withConfig, withI18n } from 'spa/Components/Context';
import { inject } from 'mobx-react';

import Typography, { TypeVariants } from '@urbanmassage/component-library/lib/Typography/Typography';
import PillOptions from '@urbanmassage/component-library/lib/PillOptions/PillOptions';
import Select from '@urbanmassage/component-library/lib/Select/Select';
import Input from '@urbanmassage/component-library/lib/Input/Input';

const UPGRADE_OPTIONS = require('./gifts-upgrade-options.json');


const GiftsSelectType = ({
  clearDefaultTreatment,
  currencySymbol,
  customAmount,
  defaultTreatment,
  giftAmount,
  giftAmountInvalid,
  gifts,
  giftTreatment,
  giftTreatmentInvalid,
  giftType,
  onSetCustomAmount,
  onSetGiftAmount,
  onSetGiftTreatment,
  onSetGiftType,
  selectedCity,
  tracker,
  giftAmountCustomInvalid,
}, { config, i18n }) => {
  const [groupedOptions, setGroupedOptions] = useState([]);
  const [giftAmountOptions, setGiftAmountOptions] = useState([]);
  const [defaultTreatmentGroup, setDefaultTreatmentGroup] = useState(null);
  const [defaultTreatmentOption, setDefaultTreatmentOption] = useState(null);
  const [isCustomAmountVisible, setIsCustomAmountVisible] = useState(false);
  const [isLoadingTreatments, setIsLoadingTreatments] = useState(false);

  const giftsListConfig = config.getValue('locale.gifts-page.vouchers-value-list', i18n.umRegion);

  const onGiftTreatmentChange = (val) => {
    const [category, groupIndex, specialityId, treatment, value] = val.split('_');
    onSetGiftTreatment(val);
    tracker.track('Changed voucher treatment on gifts page', {
      category: 'Gifts',
      label: `${category.replace(/-/g, ' ')}: ${treatment.replace(/-/g, ' ')}`,
      amount: value,
      city: selectedCity,
    });
  };

  const setDefaultGiftTreatmentFromStorage = () => {
    if (giftType === 'amount') {
      onSetGiftType('treatment');
    }

    if (giftTreatment && giftTreatment.specialityId && giftTreatment.groupIndex) {
      setDefaultTreatmentGroup(giftTreatment.groupIndex);
      setDefaultTreatmentOption(parseInt(giftTreatment.specialityId, 10));
    }
  };

  const setDefaultGiftTreatmentFromUrl = () => {
    if (giftType === 'amount') {
      onSetGiftType('treatment');
    }

    const [groupLabel, specialityId] = defaultTreatment.split('-');
    const groupIndex = groupedOptions.findIndex(item => item.label === groupLabel);
    const mainOption = groupedOptions[parseInt(groupIndex, 10)];

    // Only proceed if it's a valid option coming from the URL
    if (mainOption) {
      const option = groupedOptions[parseInt(groupIndex, 10)].options.find(item => item.id === parseInt(specialityId, 10));
      onGiftTreatmentChange(option.value);
      setDefaultTreatmentGroup(groupIndex.toString());
      setDefaultTreatmentOption(parseInt(option.id, 10));
      clearDefaultTreatment();
    }
  };

  const mapTreatmentsToSelectOptions = treatments => treatments.map((itemTreatment, index) => ({
    label: itemTreatment.categoryName,
    options: itemTreatment.options.map(option => ({
      label: option.label,
      value: `${itemTreatment.categoryName.replace(/\s+/g, '-')}_${index}_${option.speciality_id}_${option.label.replace(/\s+/g, '-')}_${option.value}`,
      id: option.speciality_id,
    })),
  }));

  const handleGiftTypeChange = (val) => {
    if (val === 'treatment') {
      setIsCustomAmountVisible(false);
      tracker.track('Clicked Gift A Treatment', {
        category: 'Gifts',
        city: selectedCity,
      });
    }
    if (val === 'amount') {
      setDefaultTreatmentGroup(null);
      setDefaultTreatmentOption(null);
      tracker.track('Clicked Gift An Amount', {
        category: 'Gifts',
        city: selectedCity,
      });
    }
    onSetGiftType(val);
  };

  const pillOptionsCfg = {
    options: [{
      name: i18n._x('Gift a treatment', 'Gifts page'),
      value: 'treatment',
      isSelected: giftType === 'treatment',
    },
    {
      name: i18n._x('Gift an amount', 'Gifts page'),
      value: 'amount',
      isSelected: giftType === 'amount',
    }],
    onChange: (value) => {
      handleGiftTypeChange(value);
    },
  };

  const getGiftTreatmentOptions = () => {
    setIsLoadingTreatments(true);
    gifts.giftVoucherTreatmentOptions.load(`${selectedCity}`).then((res) => {
      // Filtering out options that do not have valid data
      const filteredOptions = res.filter((option) => {
        if (option.categoryName && option.options && option.options.length) {
          return option;
        }

        return null;
      });

      const grouped = mapTreatmentsToSelectOptions(filteredOptions);

      setGroupedOptions(grouped);

      tracker.track('Successfully Loaded Gift Voucher Treatments Options', {
        category: 'Gifts',
      });
    }).catch((err) => {
      tracker.track('Voucher treatment options error', {
        category: 'Gifts',
        responseError: err,
      });
    }).finally(() => {
      setIsLoadingTreatments(false);
    });
  };

  const getGiftAmountOptions = () => {
    // Get the copy for selected language, if it doesn't exist default to en
    const productsStatic = giftsListConfig[selectedCity][i18n.safeLanguage] ? giftsListConfig[selectedCity][i18n.safeLanguage] : giftsListConfig[selectedCity].en;

    const list = productsStatic.map(product => ({
      value: product.total,
      label: `${currencySymbol}${product.total} - ${product.name}`,
    }));
    setGiftAmountOptions(list);
  };

  const onGiftAmountChange = (val) => {
    if (val === 'custom') {
      setIsCustomAmountVisible(true);
      return;
    }
    if (isCustomAmountVisible) {
      setIsCustomAmountVisible(false);
    }
    onSetGiftAmount(val);
    tracker.track('Changed voucher value on gifts page', {
      category: 'Gifts',
      label: val,
      city: selectedCity,
    });
  };

  const onCustomAmount = () => {
    setIsCustomAmountVisible(true);
    if (!giftAmountOptions.find(item => item.value === 'custom')) {
      const list = [...giftAmountOptions];
      list.push({
        value: 'custom',
        label: i18n._x('Custom Value', 'Gifts page - custom voucher amount'),
      });
      setGiftAmountOptions(list);
    }
    onSetGiftAmount('custom');
  };

  const durationSelected = useMemo(() => {
    if (!giftTreatment) {
      return null;
    }

    const option = UPGRADE_OPTIONS[selectedCity].find(item => item.speciality_id === Number(giftTreatment.specialityId));
    return option ? option.duration : null;
  }, [giftTreatment]);

  useEffect(() => {
    if (!selectedCity || !currencySymbol) {
      return;
    }

    getGiftTreatmentOptions();
    getGiftAmountOptions();
  }, [selectedCity, currencySymbol]);

  useEffect(() => {
    if (giftTreatment && !defaultTreatment && groupedOptions.length) {
      setDefaultGiftTreatmentFromStorage();
    }
  }, [groupedOptions.length]);

  useEffect(() => {
    if (defaultTreatment && groupedOptions.length) {
      setDefaultGiftTreatmentFromUrl();
    }
  }, [groupedOptions, defaultTreatment]);

  return (
    <div className="gifts__type-selection">
      <Typography variant={TypeVariants.h3} className="gifts__type-selection-text">
        {i18n._x('How would you like to gift?', 'Gifts page')}
      </Typography>
      <div className="gifts__type-selection-wrapper">
        <PillOptions {...pillOptionsCfg} />
      </div>
      <div className="gifts__type-selection-list-wrapper">
        {giftType === 'amount' && giftAmountOptions.length > 0 && (
          <div className="gifts__select-type-amount-wrapper">
            <div className="gifts__select-type-amount">
              <Select
                disabled={isLoadingTreatments}
                fontSize="small"
                label={i18n._x('Select a voucher', 'Gifts page')}
                name="voucherAmount"
                onChange={onGiftAmountChange}
                options={giftAmountOptions}
                placeholder={i18n._x('Please select...', 'Gifts page')}
                required
                showAddOption
                addNewOptionText="Custom"
                onSelectAddNew={onCustomAmount}
                defaultValue={giftAmount}
                invalid={giftAmountInvalid}
              />
            </div>
            {isCustomAmountVisible && (
              <div className="gifts__select-type-amount-custom">
                <Input
                  label={i18n._x('Other amount', 'Gifts page')}
                  type="number"
                  placeholder={i18n._x('Type amount', 'Gifts page')}
                  value={customAmount}
                  onChange={e => onSetCustomAmount(e.target.value)}
                  invalid={giftAmountCustomInvalid}
                />
              </div>
            )}
            <Typography variant={TypeVariants.body} className="gifts__type-selection-minimum">
              {i18n._x('Minimum amount: %(currencySymbol)s10', 'Gifts page', {
                currencySymbol,
              })}
            </Typography>
          </div>
        )}
        {giftType === 'treatment' && (
          <React.Fragment>
            <Select
              defaultGroup={defaultTreatmentGroup}
              defaultGroupOption={defaultTreatmentOption}
              disabled={isLoadingTreatments}
              fontSize="small"
              groupedOptions={groupedOptions}
              label={i18n._x('Select a treatment', 'Gifts page')}
              name="voucherTreatment"
              onChange={onGiftTreatmentChange}
              placeholder={i18n._x('Please select...', 'Gifts page')}
              required
              invalid={giftTreatmentInvalid}
            />
            {durationSelected && (
              <div className="gifts__type-selection-duration">
                <Typography variant={TypeVariants.labelMMedium}>{i18n._x('You\'ve selected a %(durationSelected)s treatment', 'Gifts page', {
                  durationSelected,
                })}</Typography>
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

GiftsSelectType.propTypes = {
  clearDefaultTreatment: PropTypes.func.isRequired,
  giftAmountCustomInvalid: PropTypes.bool,
  currencySymbol: PropTypes.string,
  customAmount: PropTypes.string,
  defaultTreatment: PropTypes.string,
  giftAmount: PropTypes.string,
  giftAmountInvalid: PropTypes.bool,
  gifts: PropTypes.object.isRequired,
  giftTreatment: PropTypes.shape({
    groupIndex: PropTypes.string,
    specialityId: PropTypes.string,
    category: PropTypes.string,
    treatment: PropTypes.string,
    value: PropTypes.string,
  }),
  giftTreatmentInvalid: PropTypes.bool,
  giftType: PropTypes.string.isRequired,
  onSetCustomAmount: PropTypes.func.isRequired,
  onSetGiftAmount: PropTypes.func.isRequired,
  onSetGiftTreatment: PropTypes.func.isRequired,
  onSetGiftType: PropTypes.func.isRequired,
  selectedCity: PropTypes.string,
  tracker: PropTypes.shape({
    track: PropTypes.func.isRequired,
  }).isRequired,
};

export default inject(({
  store: {
    gifts,
    tracker,
  },
}) => ({
  gifts,
  tracker,
}))(withConfig((withI18n(GiftsSelectType))));



// WEBPACK FOOTER //
// ./spa/Pages/Gifts/GiftsSelectType.jsx